<template>
  <div class="ap">
    <!-- my work: counters + buckets -->
    <template v-if="mode === 'mywork'">
      <div class="ap__tiles">
        <button
          v-for="b in buckets"
          :key="b.key"
          type="button"
          class="ap__tile"
          :class="{ on: bucket === b.key }"
          :style="{ '--c': b.color }"
          @click="bucket = b.key"
        >
          <v-icon small :color="b.color">{{ b.icon }}</v-icon>
          <b>{{ num(count(b.key)) }}</b>
          <span>{{ b.label }}</span>
        </button>
      </div>
    </template>

    <!-- activities: filters -->
    <div v-else class="ap__bar">
      <div class="ap__search">
        <v-icon small>mdi-magnify</v-icon>
        <input v-model="search" :placeholder="$t('crmSearchActivities')" @input="debounced">
      </div>
      <div class="ap__seg">
        <button v-for="s in statusTabs" :key="s.key" type="button" :class="{ on: status === s.key }" @click="status = s.key">{{ s.label }}</button>
      </div>
      <div class="ap__types">
        <button
          v-for="t in types"
          :key="t"
          type="button"
          class="ap__type"
          :class="{ on: typeFilter.includes(t) }"
          :title="$t('crmAct_' + t)"
          @click="toggleType(t)"
        >
          <v-icon x-small>{{ icons[t] }}</v-icon><span v-if="!isMobile">{{ $t('crmAct_' + t) }}</span>
        </button>
      </div>
      <v-spacer />
      <span class="ap__total">{{ $t('crmTotalRecords', { n: num(total) }) }}</span>
    </div>

    <div class="ap__body" @scroll="onScroll">
      <div v-if="loading && !rows.length" class="ap__loading"><v-progress-circular indeterminate color="primary" :size="22" :width="2" /></div>

      <!-- record buckets in my work -->
      <template v-if="mode === 'mywork' && isRecordBucket">
        <div v-for="r in rows" :key="r.id" class="ap__row" tabindex="0" @click="openRecord(r)" @keydown.enter="openRecord(r)">
          <span class="ap__ico" :style="{ '--c': bucketObj.color }"><v-icon x-small>{{ bucket === 'leads_not_contacted' ? 'mdi-account-outline' : 'mdi-briefcase-outline' }}</v-icon></span>
          <div class="ap__main">
            <b>{{ r.title || displayName(r) }}</b>
            <small>{{ bucket === 'leads_not_contacted' ? (r.company_name || r.mobile) : customer(r) }}</small>
          </div>
          <span class="ap__when">{{ fmtDate(r.created_at) }}</span>
          <crm-avatar :user-id="r.owner_user_id" :size="24" />
          <button type="button" class="ap__btn" @click.stop="shell.logActivity(bucket === 'leads_not_contacted' ? 'lead' : 'deal', r)">
            <v-icon x-small>mdi-plus</v-icon>{{ $t('crmFollowUp') }}
          </button>
        </div>
      </template>

      <!-- activity rows grouped by day -->
      <template v-else>
        <section v-for="g in groups" :key="g.key" class="ap__group">
          <header class="ap__group-head">{{ g.label }} <span>{{ num(g.items.length) }}</span></header>
          <div
            v-for="a in g.items"
            :key="a.id"
            class="ap__row"
            :class="{ 'ap__row--done': a.status !== 'open', 'ap__row--late': isLate(a) }"
          >
            <button
              type="button"
              class="ap__check"
              :class="{ on: a.status === 'done' }"
              :disabled="a.status === 'cancelled'"
              :title="a.status === 'open' ? $t('crmComplete') : $t('crmReopen')"
              @click="a.status === 'open' ? complete(a) : reopen(a)"
            >
              <v-icon x-small>{{ a.status === 'done' ? 'mdi-check' : (a.status === 'cancelled' ? 'mdi-close' : '') }}</v-icon>
            </button>
            <span class="ap__ico" :style="{ '--c': '#2166d1' }"><v-icon x-small>{{ icons[a.type] || 'mdi-circle-small' }}</v-icon></span>
            <div class="ap__main" @click="openEntity(a)">
              <b>{{ a.title || $t('crmAct_' + a.type) }}</b>
              <small>
                <template v-if="a.entity_type">{{ $t('crmEntity_' + a.entity_type) }}{{ entityTitle(a) ? ': ' + entityTitle(a) : '' }}</template>
                <template v-if="a.result"> · {{ a.result }}</template>
              </small>
            </div>
            <crm-chip v-if="a.priority && a.priority !== 'normal'" kind="priority" :value="a.priority" small class="ap__hide-sm" />
            <span class="ap__when" :class="{ late: isLate(a) }">{{ fmtDate(a.due_at || a.completed_at || a.created_at, true) }}</span>
            <crm-avatar :user-id="a.owner_user_id" :size="24" class="ap__hide-sm" />
            <v-menu v-if="a.status === 'open'" offset-y left>
              <template #activator="{ on }">
                <button type="button" class="ap__more" v-on="on"><v-icon small>mdi-dots-vertical</v-icon></button>
              </template>
              <v-list dense>
                <v-list-item @click="snooze(a, 60)"><v-icon small class="me-2">mdi-clock-outline</v-icon>{{ $t('crmSnooze1h') }}</v-list-item>
                <v-list-item @click="snooze(a, 1440)"><v-icon small class="me-2">mdi-calendar-arrow-left</v-icon>{{ $t('crmSnooze1d') }}</v-list-item>
                <v-list-item @click="cancel(a)"><v-icon small class="me-2">mdi-cancel</v-icon>{{ $t('crmCancelActivity') }}</v-list-item>
              </v-list>
            </v-menu>
          </div>
        </section>
      </template>

      <div v-if="!loading && !rows.length" class="ap__empty">
        <v-icon size="40" color="primary">mdi-check-all</v-icon>
        <b>{{ $t('crmNothingHere') }}</b>
      </div>
      <div v-if="loading && rows.length" class="ap__loading"><v-progress-circular indeterminate color="primary" :size="20" :width="2" /></div>
    </div>
  </div>
</template>

<script>
import moment from 'jalali-moment'
import api, { crmError } from '../crmApi'
import { pick, fmtDate, fullName, dealCustomer, isFa, ACTIVITY_TYPES, ACTIVITY_ICONS } from '../crmStore'
import { CrmChip, CrmAvatar } from '../components/CrmBits'

const RECORD_BUCKETS = ['deals_without_follow_up', 'leads_not_contacted']

export default {
  name: 'CrmActivitiesPage',
  components: { CrmChip, CrmAvatar },
  inject: ['shell'],
  props: { mode: { type: String, default: 'activities' } },
  data: () => ({
    work: null,
    bucket: 'today',
    rows: [],
    total: 0,
    page: 1,
    lastPage: 1,
    loading: false,
    search: '',
    status: 'open',
    typeFilter: [],
    timer: null
  }),
  computed: {
    types () { return ACTIVITY_TYPES },
    icons () { return ACTIVITY_ICONS },
    isRecordBucket () { return RECORD_BUCKETS.includes(this.bucket) },
    buckets () {
      return [
        { key: 'overdue', icon: 'mdi-alarm', color: '#c0392b', label: this.$t('crmGroupOverdue') },
        { key: 'today', icon: 'mdi-calendar-today', color: '#2166d1', label: this.$t('crmGroupToday') },
        { key: 'upcoming', icon: 'mdi-calendar-arrow-left', color: '#0f9d76', label: this.$t('crmGroupUpcoming') },
        { key: 'without_due_date', icon: 'mdi-calendar-blank-outline', color: '#8a94a6', label: this.$t('crmNoDueDate') },
        { key: 'deals_without_follow_up', icon: 'mdi-briefcase-remove-outline', color: '#d9822b', label: this.$t('crmDealsNoFollow') },
        { key: 'leads_not_contacted', icon: 'mdi-phone-off-outline', color: '#6a4fd1', label: this.$t('crmLeadsNotContacted') }
      ]
    },
    bucketObj () { return this.buckets.find(b => b.key === this.bucket) || this.buckets[0] },
    statusTabs () {
      return [
        { key: 'open', label: this.$t('crmStatus_open') },
        { key: 'overdue', label: this.$t('crmOverdue') },
        { key: 'done', label: this.$t('crmActDone') },
        { key: 'all', label: this.$t('crmAll') }
      ]
    },
    groups () {
      if (this.mode === 'mywork') return [{ key: this.bucket, label: this.bucketObj.label, items: this.rows }]
      const map = {}
      this.rows.forEach(a => {
        const d = (a.due_at || a.completed_at || a.created_at || '').slice(0, 10) || 'none'
        if (!map[d]) map[d] = []
        map[d].push(a)
      })
      return Object.keys(map).map(k => ({ key: k, label: this.dayLabel(k), items: map[k] }))
    }
  },
  watch: {
    bucket () { this.useBucket() },
    status () { this.reload() },
    typeFilter () { this.reload() }
  },
  created () {
    this.shell.$on('changed', this.reload)
    this.reload()
  },
  beforeDestroy () { this.shell.$off('changed', this.reload) },
  methods: {
    fmtDate,
    num (v) { return Number(v || 0).toLocaleString(isFa() ? 'fa-IR' : 'en-US') },
    displayName (r) { return fullName(r) || r.company_name || '#' + r.id },
    customer (r) { return dealCustomer(r) },
    count (k) {
      const w = this.work || {}
      if (w.counts && w.counts[k] !== undefined) return w.counts[k]
      return Array.isArray(w[k]) ? w[k].length : 0
    },
    isLate (a) { return a.status === 'open' && a.due_at && moment(a.due_at, 'YYYY-MM-DD HH:mm:ss').isBefore(moment()) },
    dayLabel (k) {
      if (k === 'none') return this.$t('crmNoDueDate')
      const m = moment(k, 'YYYY-MM-DD')
      const diff = m.clone().startOf('day').diff(moment().startOf('day'), 'days')
      if (diff === 0) return this.$t('crmGroupToday')
      if (diff === 1) return this.$t('crmTomorrow')
      if (diff === -1) return this.$t('crmYesterday')
      return isFa() ? m.locale('fa').format('dddd jD jMMMM') : m.locale('en').format('dddd D MMM')
    },
    entityTitle (a) {
      const e = a.entity || a.deal || a.lead || null
      if (!e) return ''
      return e.title || fullName(e) || e.company_name || e.name || ''
    },
    debounced () {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => this.reload(), 350)
    },
    toggleType (t) {
      this.typeFilter = this.typeFilter.includes(t) ? this.typeFilter.filter(x => x !== t) : this.typeFilter.concat(t)
    },
    reload () {
      if (this.mode === 'mywork') return this.loadWork()
      this.page = 1
      this.rows = []
      return this.loadList()
    },
    async loadWork () {
      this.loading = true
      try {
        this.work = await api.myWork()
        if (!this.count(this.bucket) && this.count('overdue')) this.bucket = 'overdue'
        this.useBucket()
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    useBucket () {
      const w = this.work || {}
      this.rows = pick(w[this.bucket])
      this.total = this.rows.length
    },
    async loadList () {
      this.loading = true
      try {
        const p = { page: this.page, itemsPerPage: 40, search: this.search || undefined }
        if (this.status === 'overdue') { p.overdue = 1; p.status = ['open'] } else if (this.status !== 'all') p.status = [this.status]
        if (this.typeFilter.length) p.type = this.typeFilter
        const res = await api.activities(p)
        const items = pick(res)
        this.rows = this.page === 1 ? items : this.rows.concat(items)
        this.total = res.total || this.rows.length
        this.lastPage = res.last_page || 1
      } catch (e) {
        this.shell.toast(crmError(e).message, { error: true })
      } finally {
        this.loading = false
      }
    },
    onScroll (e) {
      if (this.mode === 'mywork') return
      const el = e.target
      if (!this.loading && this.page < this.lastPage && el.scrollTop + el.clientHeight > el.scrollHeight - 200) {
        this.page++
        this.loadList()
      }
    },
    async complete (a) {
      const result = await this.shell.ask(this.$t('crmComplete') + ': ' + (a.title || this.$t('crmAct_' + a.type)), this.$t('crmActResult'), '—')
      if (result === null) return
      try {
        await api.completeActivity(a.id, { result })
        a.status = 'done'
        this.shell.toast(this.$t('crmSaved'), { undo: () => api.reopenActivity(a.id).then(() => this.reload()) })
        if (this.mode === 'mywork') this.reload()
      } catch (e) { this.shell.toast(crmError(e).message, { error: true }) }
    },
    async reopen (a) {
      try { await api.reopenActivity(a.id); a.status = 'open' } catch (e) { this.shell.toast(crmError(e).message, { error: true }) }
    },
    async cancel (a) {
      const reason = await this.shell.ask(this.$t('crmCancelActivity'), this.$t('crmReason'))
      if (reason === null) return
      try {
        await api.cancelActivity(a.id, { reason })
        this.shell.toast(this.$t('crmSaved'), { undo: () => api.reopenActivity(a.id).then(() => this.reload()) })
        this.reload()
      } catch (e) { this.shell.toast(crmError(e).message, { error: true }) }
    },
    async snooze (a, minutes) {
      try {
        await api.snoozeActivity(a.id, { minutes })
        this.shell.toast(this.$t('crmSaved'))
        this.reload()
      } catch (e) { this.shell.toast(crmError(e).message, { error: true }) }
    },
    openEntity (a) {
      if (a.entity_type === 'deal' || a.entity_type === 'lead') this.shell.openRecord(a.entity_type, a.entity || { id: a.entity_id })
    },
    openRecord (r) {
      this.shell.openRecord(this.bucket === 'leads_not_contacted' ? 'lead' : 'deal', r)
    }
  }
}
</script>

<style scoped>
.ap { display: flex; flex-direction: column; gap: 12px; height: 100%; min-height: 0; }
.ap__tiles { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 8px; flex-shrink: 0; }
.ap__tile { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 12px 14px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .08); text-align: start; transition: all .15s; }
.theme--dark .ap__tile { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.ap__tile b { font-size: 1.3rem; font-weight: 800; color: var(--c); }
.ap__tile span { font-size: .74rem; opacity: .7; }
.ap__tile.on { border-color: var(--c); box-shadow: inset 0 -3px 0 var(--c); }
.ap__bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; flex-shrink: 0; }
.ap__search { display: flex; align-items: center; gap: 8px; height: 38px; padding: 0 12px; border-radius: 6px; background: #fff; border: 1px solid rgba(15, 23, 42, .1); width: 240px; }
.theme--dark .ap__search { background: #1e1e1e; }
.ap__search input { flex: 1; min-width: 0; outline: none; font-size: .85rem; color: inherit; }
.ap__seg { display: inline-flex; padding: 3px; border-radius: 6px; background: rgba(15, 23, 42, .06); }
.ap__seg button { height: 32px; padding: 0 12px; border-radius: 6px; font-size: .8rem; font-weight: 600; opacity: .65; }
.ap__seg button.on { background: #fff; opacity: 1; color: var(--v-primary-base); box-shadow: 0 1px 3px rgba(15, 23, 42, .12); }
.theme--dark .ap__seg button.on { background: #2b2b2b; }
.ap__types { display: flex; gap: 4px; flex-wrap: wrap; }
.ap__type { display: inline-flex; align-items: center; gap: 4px; height: 28px; padding: 0 8px; border-radius: 6px; border: 1px solid rgba(15, 23, 42, .1); font-size: .74rem; }
.ap__type.on { border-color: var(--v-primary-base); color: var(--v-primary-base); background: rgba(25, 118, 210, .08); }
.ap__type.on ::v-deep .v-icon { color: var(--v-primary-base) !important; }
.ap__total { font-size: .78rem; opacity: .65; }
.ap__body { flex: 1; min-height: 0; overflow-y: auto; overflow-x: hidden; padding-bottom: 20px; }
.ap__group { margin-bottom: 16px; }
.ap__group-head { position: sticky; top: 0; z-index: 1; background: #f3f5f9; padding: 6px 4px 8px; font-size: .84rem; font-weight: 700; }
.theme--dark .ap__group-head { background: #121212; }
.ap__group-head span { font-size: .7rem; padding: 0 7px; border-radius: 6px; background: rgba(127, 127, 127, .14); font-weight: 600; }
.ap__row { display: flex; align-items: center; gap: 10px; padding: 9px 12px; background: #fff; border: 1px solid rgba(15, 23, 42, .07); border-radius: 6px; margin-bottom: 6px; cursor: pointer; outline: none; transition: box-shadow .15s; }
.ap__row:hover, .ap__row:focus-visible { box-shadow: 0 3px 10px rgba(15, 23, 42, .08); }
.theme--dark .ap__row { background: #1e1e1e; border-color: rgba(255, 255, 255, .08); }
.ap__row--done { opacity: .6; }
.ap__row--done .ap__main b { text-decoration: line-through; }
.ap__row--late { border-inline-start: 3px solid #c0392b; }
.ap__check { width: 22px; height: 22px; border-radius: 50%; border: 2px solid rgba(127, 127, 127, .45); display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all .15s; }
.ap__check:hover { border-color: #0f9d76; }
.ap__check.on { background: #0f9d76; border-color: #0f9d76; }
.ap__check.on ::v-deep .v-icon { color: #fff !important; }
.ap__ico { width: 28px; height: 28px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; background: rgba(25, 118, 210, .1); flex-shrink: 0; }
.ap__ico ::v-deep .v-icon { color: var(--c) !important; }
.ap__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.ap__main b { font-size: .85rem; }
.ap__main small { font-size: .74rem; opacity: .6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ap__when { font-size: .74rem; opacity: .7; white-space: nowrap; }
.ap__when.late { color: #c0392b; opacity: 1; font-weight: 600; }
.ap__more { width: 28px; height: 28px; border-radius: 6px; display: inline-flex; align-items: center; justify-content: center; }
.ap__more:hover { background: rgba(127, 127, 127, .12); }
.ap__btn { display: inline-flex; align-items: center; gap: 4px; height: 28px; padding: 0 10px; border-radius: 6px; border: 1px solid rgba(25, 118, 210, .3); color: var(--v-primary-base); font-size: .75rem; font-weight: 600; }
.ap__empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 48px 0; opacity: .8; }
.ap__loading { display: flex; justify-content: center; padding: 16px; }
@media (max-width: 1000px) { .ap__tiles { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 799px) {
  .ap__tiles { display: flex; overflow-x: auto; }
  .ap__tile { flex: 0 0 120px; }
  .ap__search { width: 100%; }
  .ap__hide-sm { display: none !important; }
}
</style>
